import React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { translations } from '@/utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// German + Norwegian are only enabled on these page roots. On any other route,
// t() falls back to English even if the user picked de or no, so other pages
// stay untouched while we roll language support out gradually.
const RESTRICTED_LANGS = new Set(['de', 'no']);
const RESTRICTED_LANG_ROUTES = [
  '/home',
  '/nonverbal',
  '/settings',
  '/unknown-situation',
  '/custom-situation',
  '/push-to-talk',
  // Timo's kid page and the parent dashboard. /timo was missing here, which
  // meant German and Norwegian families silently got an English Timo: his
  // greeting, the word of the day, and every caption fell back to en.
  '/timo',
  '/parent',
];

function isRestrictedLangAllowed(pathname) {
  return RESTRICTED_LANG_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('ttt-language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('ttt-language', language);
    // Attribution everywhere: register app_language as a PostHog super-property
    // so every event (autocaptured or named) carries the active UI language.
    try {
      if (typeof posthog.register === 'function') {
        posthog.register({ app_language: language });
      }
    } catch (_) { /* posthog not ready / opted out: non-fatal */ }
  }, [language]);

  const t = (key) => {
    let effectiveLang = language;
    if (RESTRICTED_LANGS.has(effectiveLang)) {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (!isRestrictedLangAllowed(pathname)) {
        effectiveLang = 'en';
      }
    }
    return translations[effectiveLang]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
